import styled from '@emotion/styled';
import { Box, Button as _Button } from '@material-ui/core';
import { parse } from 'qs';
import { ClassAttributes, createElement, useContext } from 'react';
import { StateHandler } from 'recompose';

import { CreationForShowFragment, ProjectDetailsForShowFragment } from '../../../../generated/graphql';
import { SingleAssignmentFragment } from '../../../../generated/graphql_offer';
import { getLatestDeliverable } from '../../../../graphql/creation';
import { isDemo } from '../../../../graphql/project';
import AvatarAndName from '../../../component/compound/AvatarAndName';
import CreationsList, { Item as CreationsListItem } from '../../../component/compound/CreationsList';
import _ReputationDetail from '../../../component/compound/ReputationDetail';
import { SPACING_UNIT } from '../../../constant/spacing';
import { context as ConfigContext } from '../../../context/config';
import { context as dependencyContext } from '../../../context/dependency';
import { context as stateContext } from '../../../context/state';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import { default as _CompleteForm } from './CompleteForm';

type Props = ClassAttributes<HTMLElement> & {
  project: ProjectDetailsForShowFragment;
  visibleCreationsByAssignment: {
    assignment: SingleAssignmentFragment;
    creations: CreationForShowFragment[];
  }[];
  setSelectedCreationId: StateHandler<string>;
  reload: () => Promise<unknown>;
  className?: string;
};

const AssignmentList = ({ className, setSelectedCreationId, project, visibleCreationsByAssignment, reload }: Props) => {
  const dependency = useContext(dependencyContext);
  const state = useContext(stateContext);
  const config = useContext(ConfigContext);
  const query = parse(state.location.search.slice(1));
  const organizationId = query.org;

  const fetchAmazonUploadedCreatives = dependency.integrateAmazonAdAccountUsecase.fetchUploadedAmazonCreatives;
  const fetchYahooUploadedCreatives = dependency.integrateYahooAdAccountUsecase.fetchUploadedYahooCreatives;
  const fetchTiktokUploadedCreatives = dependency.integrateTiktokAdAccountUsecase.fetchUploadedTiktokCreatives;

  const profileUrl = config.PROFILE_WEB_URL;

  return (
    <div className={className}>
      {visibleCreationsByAssignment.map(({ assignment, creations }, index) => (
        <AssignmentSection
          key={`a_${assignment.id}`}
          borderBottom={visibleCreationsByAssignment.length - 1 == index ? 0 : 1}
        >
          <AssignmentHeader>
            <ProfileLink variant="text" href={`${profileUrl}/users/${assignment.user.id}`} target="_blank">
              <AvatarAndName user={assignment.user} />
            </ProfileLink>
            {isDemo(project) ? null : assignment.reputation ? (
              <ReputationDetail
                directorReputation={assignment.reputation ?? null}
                optimizerReputation={assignment.reputationToOffer ?? null}
                loadingReputation={false}
              />
            ) : null}
            {isDemo(project) ? null : <CompleteForm assignment={assignment} reload={reload} />}
          </AssignmentHeader>
          <CreationsList proposal={false}>
            {creations.map((creation) => {
              const latestDeliverable = getLatestDeliverable(creation)!;
              return (
                <CreationsListItem
                  assignmentState={assignment.status}
                  creation={creation}
                  onClickSendButton={() => {
                    fetchAmazonUploadedCreatives(latestDeliverable.creative.id, organizationId);
                    fetchYahooUploadedCreatives(latestDeliverable.creative.id, organizationId);
                    fetchTiktokUploadedCreatives(latestDeliverable.creative.id, organizationId);
                    setSelectedCreationId(creation.id);
                  }}
                  requestLatestDeliverableCreativeUrl={() => creation.latestDeliverableUrl}
                  key={creation.id}
                  organizationId={organizationId}
                />
              );
            })}
          </CreationsList>
        </AssignmentSection>
      ))}
    </div>
  );
};

const AssignmentHeader = styled('div')(
  gridLayoutContainer({
    columns: ['300px', '600px'], // `calc(100% - 480px)`
    rows: ['auto'],
    areas: [['profile', 'reputation']],
  }),
);

const ProfileLink = (styled(_Button)(gridLayoutItem('profile'), {
  padding: SPACING_UNIT,
  textTransform: 'none',
}) as unknown) as typeof _Button;

const ReputationDetail = (styled(_ReputationDetail)(gridLayoutItem('reputation'), {
  padding: SPACING_UNIT,
}) as unknown) as typeof _ReputationDetail;

const CompleteForm = (styled(_CompleteForm)({
  padding: SPACING_UNIT,
}) as unknown) as typeof _CompleteForm;

const AssignmentSection = (styled(Box)({
  padding: SPACING_UNIT,
}) as unknown) as typeof Box;

export default AssignmentList;
